import { CSSProperties, FC, ReactNode, useMemo } from 'react';

import {
  Icon,
  Typography,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import clsx from 'clsx';
import { BookingState, Circle, Room, RoomType } from 'src/types';

import classes from './RoomPlace.module.scss';

const contentGapCoef = 0.5;

export interface RoomPlaceProps {
  item: Room;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
}

export const RoomPlace: FC<RoomPlaceProps> = ({
  item,
  className,
  contentClassName,
  disabled,
}) => {
  const { width, height, fontSize } = item;

  const x = item.x - width / 2;
  const y = item.y - height / 2;

  const containerStyle = useMemo(
    (): CSSProperties => ({
      transform: `translate(${x}px,${y}px)`,
      width: `${width}px`,
      height: `${height}px`,
    }),
    [height, width, x, y],
  );

  const fontSizePx = `${fontSize}px`;
  const titleStyle = useMemo(
    (): CSSProperties => ({
      fontSize: fontSizePx,
      lineHeight: fontSizePx,
    }),
    [fontSizePx],
  );

  const iconStyle = useMemo(
    (): CSSProperties => ({
      fontSize: fontSizePx,
      width: fontSizePx,
      height: fontSizePx,
      lineHeight: fontSizePx,
    }),
    [fontSizePx],
  );

  const contentStyle = useMemo(
    (): CSSProperties => ({
      gap: `${fontSize * contentGapCoef}px`,
    }),
    [fontSize],
  );
  const id = item.mapPlaceId;
  const placeId = disabled ? undefined : id;

  const info = useMemo((): ReactNode => {
    if (
      item.status?.state === BookingState.BookingInProgress ||
      item.status?.state === BookingState.UnbookingInProgress
    )
      return (
        <Icon
          className={classes.text}
          iconName={Icons.Hourglass}
          style={iconStyle}
        />
      );

    switch (item.roomType) {
      case RoomType.Focus:
        return (
          <Typography
            className={classes.text}
            variant='body3'
            style={titleStyle}
          >
            Ф
          </Typography>
        );
      case RoomType.Phone:
        return (
          <Icon
            className={classes.text}
            iconName={Icons.Phone}
            style={iconStyle}
          />
        );
      case RoomType.Meeting:
        return (
          <>
            <Icon
              className={classes.text}
              iconName={Icons.User}
              style={iconStyle}
            />
            <Typography
              className={classes.text}
              variant='body3'
              style={titleStyle}
            >
              {item.seatCount}
            </Typography>
          </>
        );
    }
  }, [iconStyle, item.roomType, item.seatCount, item.status, titleStyle]);

  return (
    <div
      className={clsx(
        classes.container,
        disabled ? classes.disabled : classes.enabled,
        className,
      )}
      style={containerStyle}
      data-testid={disabled ? undefined : `place-${id}`}
      data-place-id={placeId}
      data-title={item.title}
    >
      <div
        className={clsx(classes.content, contentClassName)}
        style={contentStyle}
      >
        {item.title && (
          <Typography
            variant='subtitle3'
            className={classes.text}
            style={titleStyle}
          >
            {item.title}
          </Typography>
        )}
        <div className={classes.info}>{info}</div>
      </div>
    </div>
  );
};

export const roomToCircle = (room: Room): Circle => {
  const halfWidth = room.width / 2;

  return {
    type: 'circle',
    x: room.x,
    y: room.y,
    radius: halfWidth,
  };
};
